import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Mail, 
  Users, 
  Briefcase, 
  Phone, 
  Calendar, 
  Building2,
  MapPin,
  Clock,
  FileText,
  AlertCircle
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';

const SubmissionsPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/submissions/${token}`);
        setData(response.data);
      } catch (err) {
        setError('Bu sayfaya erişim yetkiniz yok veya link geçersiz.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#005CB2] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Erişim Reddedildi</h1>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* noindex meta tag */}
      <head>
        <meta name="robots" content="noindex, nofollow" />
      </head>
      
      <div className="min-h-screen bg-slate-100 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Form Başvuruları</h1>
            <p className="text-slate-600">Tüm form gönderimlerini buradan görüntüleyebilirsiniz</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data?.contact_messages?.length || 0}</p>
                  <p className="text-slate-600">İletişim Mesajı</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data?.personnel_requests?.length || 0}</p>
                  <p className="text-slate-600">Personel Talebi</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data?.job_applications?.length || 0}</p>
                  <p className="text-slate-600">İş Başvurusu</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data?.quote_requests?.length || 0}</p>
                  <p className="text-slate-600">Teklif Talebi</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="messages" className="space-y-4">
            <TabsList className="bg-white p-1 rounded-lg shadow">
              <TabsTrigger value="messages" className="gap-2">
                <Mail className="w-4 h-4" />
                İletişim ({data?.contact_messages?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="personnel" className="gap-2">
                <Users className="w-4 h-4" />
                Personel Talepleri ({data?.personnel_requests?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="jobs" className="gap-2">
                <Briefcase className="w-4 h-4" />
                İş Başvuruları ({data?.job_applications?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="quotes" className="gap-2">
                <FileText className="w-4 h-4" />
                Teklif Talepleri ({data?.quote_requests?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* Contact Messages */}
            <TabsContent value="messages">
              <div className="space-y-4">
                {data?.contact_messages?.length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-slate-500">Henüz mesaj yok</CardContent></Card>
                ) : (
                  data?.contact_messages?.map((msg, i) => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">{msg.name}</h3>
                            <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                              <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{msg.email}</span>
                              {msg.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{msg.phone}</span>}
                            </div>
                          </div>
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(msg.created_at)}
                          </Badge>
                        </div>
                        {msg.subject && <p className="font-medium text-slate-700 mb-2">{msg.subject}</p>}
                        <p className="text-slate-600 bg-slate-50 p-4 rounded-lg">{msg.message}</p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Personnel Requests */}
            <TabsContent value="personnel">
              <div className="space-y-4">
                {data?.personnel_requests?.length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-slate-500">Henüz personel talebi yok</CardContent></Card>
                ) : (
                  data?.personnel_requests?.map((req, i) => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">{req.company_name}</h3>
                            <p className="text-slate-600">{req.contact_person}</p>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-green-100 text-green-800 mb-1">{req.count || req.staff_count} Kişi</Badge>
                            <p className="text-xs text-slate-500">{formatDate(req.created_at)}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{req.phone}</p>
                            <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{req.email}</p>
                            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{req.location}</p>
                          </div>
                          <div className="space-y-2">
                            <p className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-slate-400" />{req.position}</p>
                            <p className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" />{req.work_type}</p>
                            <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" />{req.experience_required}</p>
                          </div>
                        </div>
                        {req.description && (
                          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-600">{req.description}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Job Applications */}
            <TabsContent value="jobs">
              <div className="space-y-4">
                {data?.job_applications?.length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-slate-500">Henüz iş başvurusu yok</CardContent></Card>
                ) : (
                  data?.job_applications?.map((job, i) => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">{job.full_name}</h3>
                            <p className="text-[#005CB2] font-medium">{job.desired_position}</p>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-purple-100 text-purple-800 mb-1">{job.work_type_preference}</Badge>
                            <p className="text-xs text-slate-500">{formatDate(job.created_at)}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
                          <div className="space-y-2">
                            <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{job.phone}</p>
                            <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{job.email}</p>
                            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{job.city}</p>
                          </div>
                          <div className="space-y-2">
                            <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" />Doğum: {job.birth_date}</p>
                            <p className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" />{job.education_level}</p>
                            <p className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-slate-400" />{job.experience_years} deneyim</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {job.has_driving_license && <Badge variant="outline">Ehliyet Var</Badge>}
                          {job.has_src_certificate && <Badge variant="outline">SRC Belgesi</Badge>}
                          {job.has_forklift_certificate && <Badge variant="outline">Forklift Belgesi</Badge>}
                        </div>
                        {job.previous_experience && (
                          <div className="p-4 bg-slate-50 rounded-lg mb-2">
                            <p className="text-xs font-semibold text-slate-500 mb-1">Önceki Deneyim</p>
                            <p className="text-sm text-slate-600">{job.previous_experience}</p>
                          </div>
                        )}
                        {job.additional_notes && (
                          <div className="p-4 bg-blue-50 rounded-lg">
                            <p className="text-xs font-semibold text-blue-500 mb-1">Ek Notlar</p>
                            <p className="text-sm text-slate-600">{job.additional_notes}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Quote Requests */}
            <TabsContent value="quotes">
              <div className="space-y-4">
                {data?.quote_requests?.length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-slate-500">Henüz teklif talebi yok</CardContent></Card>
                ) : (
                  data?.quote_requests?.map((quote, i) => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">{quote.company_name}</h3>
                            <p className="text-slate-600">{quote.contact_person}</p>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-orange-100 text-orange-800 mb-1">{quote.staff_count} Kişi</Badge>
                            <p className="text-xs text-slate-500">{formatDate(quote.created_at)}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
                          <div className="space-y-2">
                            <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{quote.phone}</p>
                            <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{quote.email}</p>
                          </div>
                          <div className="space-y-2">
                            <p><strong>Ulaşım:</strong> {quote.transportation || 'Belirtilmedi'}</p>
                            <p><strong>Kıyafet:</strong> {quote.clothing || 'Belirtilmedi'}</p>
                            <p><strong>Yemek:</strong> {quote.food || 'Belirtilmedi'}</p>
                          </div>
                        </div>
                        {quote.notes && (
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-xs font-semibold text-slate-500 mb-1">Notlar</p>
                            <p className="text-sm text-slate-600">{quote.notes}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default SubmissionsPage;
